import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import { useChefsLab } from '../../context/ChefsLabContext';

// Settings icon
const SettingsIcon = ({ color }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

// Close icon
const CloseIcon = ({ color }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

// Toggle component
const Toggle = ({ value, onChange, label, description }) => {
  const { theme } = useTheme();

  const styles = {
    container: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '12px 0',
      borderBottom: `1px solid ${theme.border}`,
    },
    labelContainer: {
      flex: 1,
    },
    label: {
      color: theme.text,
      fontSize: '14px',
      fontWeight: 500,
      margin: 0,
    },
    description: {
      color: theme.textMuted,
      fontSize: '12px',
      margin: '2px 0 0 0',
    },
    toggle: {
      width: '44px',
      height: '24px',
      borderRadius: '12px',
      backgroundColor: value ? theme.accent : theme.bgInput,
      border: `1px solid ${value ? theme.accent : theme.border}`,
      position: 'relative',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    },
    toggleKnob: {
      position: 'absolute',
      top: '2px',
      left: value ? '22px' : '2px',
      width: '18px',
      height: '18px',
      borderRadius: '50%',
      backgroundColor: '#fff',
      transition: 'left 0.2s ease',
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.labelContainer}>
        <p style={styles.label}>{label}</p>
        {description && <p style={styles.description}>{description}</p>}
      </div>
      <div style={styles.toggle} onClick={() => onChange(!value)}>
        <div style={styles.toggleKnob} />
      </div>
    </div>
  );
};

const SettingsWheel = () => {
  const { theme } = useTheme();
  const {
    preferences,
    setIncludePreference,
    resetPreferences,
  } = useChefsLab();

  const [isOpen, setIsOpen] = useState(false);

  const styles = {
    trigger: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '40px',
      height: '40px',
      borderRadius: '12px',
      backgroundColor: theme.bgCard,
      border: `1px solid ${theme.border}`,
      cursor: 'pointer',
    },
    backdrop: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: theme.overlay,
      zIndex: 1000,
    },
    modal: {
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      backgroundColor: theme.bgSheet,
      borderTopLeftRadius: '20px',
      borderTopRightRadius: '20px',
      maxHeight: '70vh',
      display: 'flex',
      flexDirection: 'column',
      zIndex: 1001,
    },
    handle: {
      width: '40px',
      height: '4px',
      backgroundColor: theme.border,
      borderRadius: '2px',
      margin: '12px auto',
      flexShrink: 0,
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 20px 16px',
      borderBottom: `1px solid ${theme.border}`,
      flexShrink: 0,
    },
    title: {
      color: theme.text,
      fontSize: '18px',
      fontWeight: 600,
      margin: 0,
    },
    closeButton: {
      background: 'none',
      border: 'none',
      padding: '4px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    content: {
      padding: '20px',
      overflowY: 'auto',
      flex: 1,
      minHeight: 0,
    },
    section: {
      marginBottom: '24px',
    },
    sectionTitle: {
      color: theme.textMuted,
      fontSize: '12px',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      margin: '0 0 12px 0',
    },
    sectionDescription: {
      color: theme.textSecondary,
      fontSize: '13px',
      margin: '0 0 16px 0',
    },
    footer: {
      padding: '16px 20px',
      paddingBottom: 'calc(16px + env(safe-area-inset-bottom))',
      borderTop: `1px solid ${theme.border}`,
      flexShrink: 0,
    },
    resetButton: {
      width: '100%',
      padding: '12px',
      borderRadius: '10px',
      backgroundColor: theme.bgInput,
      border: `1px solid ${theme.border}`,
      color: theme.textMuted,
      fontSize: '14px',
      fontWeight: 500,
      cursor: 'pointer',
    },
  };

  return (
    <>
      <button style={styles.trigger} onClick={() => setIsOpen(true)}>
        <SettingsIcon color={theme.textMuted} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              style={styles.backdrop}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
            />
            <motion.div
              style={styles.modal}
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            >
              <div style={styles.handle} />

              <div style={styles.header}>
                <div>
                  <h2 style={styles.title}>Preferences</h2>
                  <p style={{ color: theme.textMuted, fontSize: '12px', margin: '4px 0 0 0' }}>
                    All in or all out - changes apply immediately
                  </p>
                </div>
                <button style={styles.closeButton} onClick={() => setIsOpen(false)}>
                  <CloseIcon color={theme.textMuted} />
                </button>
              </div>

              <div style={styles.content}>
                {/* Include Preferences */}
                <div style={styles.section}>
                  <h3 style={styles.sectionTitle}>Include in Results</h3>
                  <p style={styles.sectionDescription}>
                    Toggle what's shown and calculated
                  </p>

                  <Toggle
                    label="Include All"
                    description="Toggle all options at once"
                    value={preferences.include?.outliers && preferences.include?.flagged && preferences.include?.rejected && preferences.include?.reported}
                    onChange={(v) => {
                      setIncludePreference('outliers', v);
                      setIncludePreference('flagged', v);
                      setIncludePreference('rejected', v);
                      setIncludePreference('reported', v);
                    }}
                  />
                  <Toggle
                    label="Outliers"
                    description="Price anomalies (unusually high/low)"
                    value={preferences.include?.outliers || false}
                    onChange={(v) => setIncludePreference('outliers', v)}
                  />
                  <Toggle
                    label="Flagged"
                    description="Listings needing review"
                    value={preferences.include?.flagged || false}
                    onChange={(v) => setIncludePreference('flagged', v)}
                  />
                  <Toggle
                    label="Rejected"
                    description="Low confidence matches (<70%)"
                    value={preferences.include?.rejected || false}
                    onChange={(v) => setIncludePreference('rejected', v)}
                  />
                  <Toggle
                    label="Reported"
                    description="Listings you've reported"
                    value={preferences.include?.reported || false}
                    onChange={(v) => setIncludePreference('reported', v)}
                  />
                </div>
              </div>

              <div style={styles.footer}>
                <button style={styles.resetButton} onClick={resetPreferences}>
                  Reset to CardChase Defaults
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default SettingsWheel;
